import {FabSpec, PageFragment, PageFragmentProps, PageFragmentState} from "../../shared/PageFragment";
import React, {ReactElement} from "react";
import {PageWithSidebarContainer, PageWithSidebarContainerRenderer} from "../../shared/PageWithSidebarContainer";
import {Sidebar, SidebarItem, SidebarListener} from "../../shared/Sidebar";
import {BaseContent, Collection, CollectionsRegistry} from "./types";
import {Add, ArticleOutlined} from "@mui/icons-material";
import {EditContentHelper} from "./BaseEditContentFragment";

export type ContentFragmentProps = PageFragmentProps & {}

type ContentFragmentState = PageFragmentState & {
  collections: Collection<any, any>[],
  sidebarItems: SidebarItem[],
  selectedContentType: string,
}

type CollectionObject<T extends BaseContent> = {
  collection: Collection<any, any>,
  type: string,
  filter?: (content: T) => boolean,
}

export class ContentFragment extends PageFragment<ContentFragmentProps, ContentFragmentState> implements SidebarListener, PageWithSidebarContainerRenderer {

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    this.setState({
      collections: CollectionsRegistry.getInstance().get(),
    });
  }

  createFabSpec(): FabSpec {
    const object = this.getSelectedSidebarItem().object as CollectionObject<any>;
    return {
      iconType: Add,
      variant: 'extended',
      text: "Add new",
      onClick: () => EditContentHelper.addContent(object.collection),
    }
  }

  private static sidebarItemType(sidebarItem: SidebarItem) {
    return (sidebarItem.object as CollectionObject<any>).type;
  }

  componentDidUpdate(prevProps: Readonly<ContentFragmentProps>, prevState: Readonly<ContentFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (this.state.collections !== prevState.collections) {
      let collectionItems = this.state.collections.flatMap(collection => {
        const items: SidebarItem[] = [];
        items.push({
          title: collection.displayName,
          iconType: ArticleOutlined,
          object: {
            collection: collection,
            type: collection.loader.type,
          },
        });
        if (collection.filterViews?.length > 0) {
          items.push(...collection.filterViews.map(filterView => ({
            title: filterView.displayName,
            iconType: filterView.iconType,
            object: {
              collection: collection,
              type: collection.loader.type + ":" + filterView.type,
              filter: filterView.filter,
            },
          })));
        }
        return items;
      });
      const sidebarItems = [...collectionItems];
      this.setState({
        sidebarItems: sidebarItems,
        selectedContentType: sidebarItems?.length > 0 ? ContentFragment.sidebarItemType(sidebarItems[0]) : null,
      });
    }
  }

  onSelectionChanged(item: SidebarItem) {
    this.setState({
      selectedContentType: (item.object as CollectionObject<any>).type,
    });
  }

  renderContent(): React.ReactElement {
    return <PageWithSidebarContainer renderer={this}/>;
  }

  private getSelectedSidebarItem(): SidebarItem {
    return this.state.sidebarItems?.find(item => ContentFragment.sidebarItemType(item) === this.state.selectedContentType);
  }

  renderPageWithSidebarContainerContent(): ReactElement {
    const selected = this.getSelectedSidebarItem();
    const object = selected.object as CollectionObject<any>;
    const ContentTableFragmentType = object.collection.ContentTableFragmentType;
    return <ContentTableFragmentType collection={object.collection} filter={object.filter}/>;
  }

  renderPageWithSidebarContainerSidebar(): React.ReactElement {
    return <Sidebar tabs={[{
      id: "default",
      selected: this.getSelectedSidebarItem(),
      items: {
        groups: [
          {
            items: this.state.sidebarItems,
            title: "Collections",
            variant: "condensed",
          },
        ],
        containerId: "content",
      },
      listener: this,
    }]}/>;
  }
}